/**
 * MKS-315 — the summary panel under the /time grid, lifted out of
 * `TimeEntryPage` so its audience rule is one readable unit (and testable
 * without rendering the whole page).
 *
 * It used to show everybody their own internal cost rate — hours × rate,
 * "Costs", "Investment" — whether or not those numbers meant anything to them.
 * The cost columns are now for the people who act on them: admin and finance,
 * and freelancers, who invoice exactly these amounts. Everyone else gets the
 * same panel with hours per project in it and no money at all.
 */
import type { Entry } from '../../services/api/timeEntry';
import { ReportingMoney } from '../common/ReportingMoney';
import { formatHoursValue, formatLoggedVsTarget } from './totalsFormat';

interface TimesheetSummaryPanelProps {
  entries: Entry[];
  /** Working days in the viewed month × 8, for the "of X h" target. */
  maxWorkingHours: number;
  /** MKS-315 — true for admin, finance, and freelancers. */
  canSeeCosts: boolean;
  /**
   * MKS-314 follow-up — the 'YYYY-MM' the panel is showing, so every figure in
   * it converts at THAT month's rate. This panel is the easy case: a timesheet
   * is one month, so there is exactly one rate and no sum across months.
   *
   * Optional, and omitting it falls back to the newest rate in force — which
   * is what the existing tests (and any caller that predates this) get.
   */
  month?: string;
}

interface ProjectRow {
  projectName: string;
  hours: number;
  costs: number;
  rate: number;
  investment: number;
}

/**
 * Group by project AND rate, so a project whose rate changed mid-month shows
 * one row per rate rather than a blended figure nobody can reconcile.
 */
function buildProjectRows(entries: Entry[]): ProjectRow[] {
  const totals = entries.reduce(
    (acc, entry) => {
      const hourlyRate =
        entry.assignmentCosts && entry.assignmentCosts > 0
          ? entry.assignmentCosts
          : entry.userCosts || 0;
      const key = `${entry.projectId}_${hourlyRate}`;
      if (!acc[key]) {
        acc[key] = {
          projectName: entry.project.fullName || entry.project.name,
          hours: 0,
          costs: 0,
          rate: hourlyRate,
          investment: 0,
        };
      }
      acc[key].hours += entry.hours;
      acc[key].costs += entry.hours * hourlyRate;
      acc[key].investment += entry.hours * (entry.investmentRate || 0);
      return acc;
    },
    {} as Record<string, ProjectRow>
  );
  return Object.values(totals);
}

export function TimesheetSummaryPanel({
  entries,
  maxWorkingHours,
  canSeeCosts,
  month,
}: TimesheetSummaryPanelProps) {
  const showCosts = canSeeCosts;

  const projectRows = buildProjectRows(entries);
  const totalHours = projectRows.reduce((sum, p) => sum + p.hours, 0);
  const totalCosts = projectRows.reduce((sum, p) => sum + p.costs, 0);
  const totalInvestment = projectRows.reduce((sum, p) => sum + p.investment, 0);

  // D-03 (Phase 98-03): these figures come from entry.assignmentCosts /
  // entry.userCosts, the internal cost ledger, which is CZK by construction —
  // the Entry DTO carries no currency field (services/api/timeEntry.ts).
  //
  // MKS-314 follow-up: being CZK by construction is exactly WHY they can be
  // re-denominated here. What RESEARCH Pitfall 3 forbids is relabelling a
  // figure with an org-level currency; `ReportingMoney` does not relabel, it
  // CONVERTS, at the viewed month's rate, and names that rate in its tooltip.
  // With the release flag off it renders byte-identically to the `formatMoney`
  // call it replaced — hence the explicit fraction digits below.
  const money = (value: number) => (
    <ReportingMoney
      value={value}
      month={month}
      // The exact digits `formatMoney(value, 'CZK', …)` wrote here before.
      numberOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
    />
  );

  // MKS-315 — hours read the same way as in the grid above ("64.5 h").
  const hours = (value: number) => `${formatHoursValue(value)} h`;

  const totalLabel = formatLoggedVsTarget(totalHours, maxWorkingHours);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">
          {showCosts ? 'Timesheet Costs Summary' : 'Hours by Project'}
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {showCosts ? 'Hours × Rate' : 'Hours'}
              </th>
              {showCosts && (
                <>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costs
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    /* MKS-315 — "Investment" alone said nothing. The hours ×
                       the investment rate on the project assignment: the part
                       of the cost TopMonks carries itself. */
                    title="Hours × the investment rate on the project assignment — the share of the cost TopMonks carries itself"
                  >
                    Investment (own share)
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projectRows.map((project) => (
              <tr key={project.projectName} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {project.projectName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                  {showCosts ? (
                    <>
                      {hours(project.hours)} × {money(project.rate)}
                    </>
                  ) : (
                    hours(project.hours)
                  )}
                </td>
                {showCosts && (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {money(project.costs)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {project.investment > 0 ? money(project.investment) : '-'}
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr className="bg-blue-50 font-semibold">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">TOTAL:</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                {totalLabel}
              </td>
              {showCosts && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {money(totalCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {totalInvestment > 0 ? money(totalInvestment) : '-'}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
